import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import MuiAutocomplete, {AutocompleteProps as MuiAutocompleteProps, AutocompleteRenderOptionState} from '@mui/material/Autocomplete';
import {styled} from '@mui/material/styles';
import {SvgIconProps} from '@mui/material/SvgIcon';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import {ComponentType, forwardRef, HTMLAttributes, ReactElement, ReactNode} from 'react';
import {MenuItem, MenuList} from '../Menu';
import {TextField, TextFieldProps} from '../TextField/TextField';

/**
 * NOTE:
 * Look at different fuzzy-matching packages:
 * mui suggested: `match-sorter` https://mui.com/components/autocomplete/#advanced
 */

export type AutocompleteValue = {
    /**
     * The text shown in the menu list.
     * @default ""
     */
    label: string | number;
    /**
     * The value of selected item in the menu list.
     * @default ""
     */
    value: string | number;
    /**
     * Option secondary value, will be display as secondary text in the AutocompleteMenuItem
     * @default undefined;
     * */
    [key: string]: string | number;
};

type DefaultAutocompleteProps = MuiAutocompleteProps<AutocompleteValue, false, false, false, 'div'>;
type ModifiedAutocompleteProps = Omit<DefaultAutocompleteProps, 'color' | 'multiple' | 'ChipProps' | 'renderInput'>;

export interface AutocompleteProps<T = TextFieldProps> extends ModifiedAutocompleteProps {
    /**
     * Required generic type. Type currently defaults to TextFieldProps. However, if you change
     * the `renderInput`, we suggest to also update the type parameter of `AutocompleteProps`.
     * For example, we use TextFieldProps because the renderInput function returns a TextField.
     * @default TextFieldProps
     */
    InputProps?: T;
    /**
     * The default React Input Component used to render the input.
     * @default TextField
     */
    InputComponent?: ComponentType<T>;
    /**
     * Dropdown ArrowComponent
     */
    ArrowComponent?: ComponentType<SvgIconProps>;
    /**
     * Function to set display value in autocomplete
     */
    getOptionLabel?: (option: AutocompleteValue) => string;
    /**
     * Function to render menu options
     */
    renderOption?: (
        props: HTMLAttributes<HTMLLIElement>,
        option: AutocompleteValue,
        state: AutocompleteRenderOptionState,
    ) => ReactNode;
    renderInput?: DefaultAutocompleteProps['renderInput'];
}

const StyledAutocomplete = styled(MuiAutocomplete)(({theme}) => ({
    '&.MuiAutocomplete-hasPopupIcon, &.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon': {
        '& .MuiAutocomplete-inputRoot': {
            paddingRight: 0
        },

        '& .MuiAutocomplete-endAdornment': {
            position: 'relative',
            display: 'flex',
            top: 'auto',

            // repeated code from input
            '& > .MuiSvgIcon-root, & > .MuiIconButton-root': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fill: `${theme.tokens.FuelColorGray700}`,
                marginRight: 2,

                '&:last-of-type': {
                    marginRight: 7
                }
            },

            '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
                visibility: 'visible',
                height: '1.5rem',
                width: '1.5rem',
                borderRadius: '0.25rem'
            }
        }
    }

    /**
     *
     * NOTE: typecasting hack for Autocomplete styled()
     * Issue Link: https://github.com/mui/material-ui/issues/21727
     * Github Comment: https://github.com/mui/material-ui/issues/21727#issuecomment-869228390
     * Sandbox Example: https://codesandbox.io/s/pedantic-tharp-bdwj8?file=/src/Autocomplete.tsx:731-753
     *
     * */
})) as typeof MuiAutocomplete;

export const AutocompleteMenu = styled(MenuList)`
    max-width: unset;
`;

export const AutocompleteMenuItem = styled(MenuItem)({
    '& .MuiMenuItem-parsedText': {
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden'
    }
});

export const Autocomplete = forwardRef<any, AutocompleteProps>(({
    InputComponent = TextField, ArrowComponent = KeyboardArrowDown, renderOption,
    InputProps, autoHighlight = true, ListboxComponent = AutocompleteMenu, ...props
}, ref) => {
    const defaultRenderOption: AutocompleteProps['renderOption'] = (
        optionProps, option, state
    ): ReactElement => {
        const {inputValue} = state;

        const matches = match(String(option.label), inputValue, {
            insideWords: true
        });
        const parts = parse(String(option.label), matches);

        return (
            <AutocompleteMenuItem {...optionProps}>
                <div className='MuiMenuItem-parsedText'>
                    {parts.map((part, index) => part.highlight ? <strong key={index}>{part.text}</strong> : <span key={index}>{part.text}</span>)}
                </div>
            </AutocompleteMenuItem>
        );
    };

    const renderInput: DefaultAutocompleteProps['renderInput'] = ({
        inputProps: {
            // NOTE: id is omitted, other this id overwrites the desired id
            id: omittedId, ...inputProps
        }, InputProps: RenderedInputProps
    }) => (
        <InputComponent
            {...RenderedInputProps}
            {...InputProps}
            inputProps={inputProps}
        />
    );

    return (
        <StyledAutocomplete
            {...props}
            ChipProps={undefined}
            ListboxComponent={ListboxComponent}
            autoHighlight={autoHighlight}
            color={undefined}
            multiple={false}
            popupIcon={<ArrowComponent/>}
            ref={ref}
            renderInput={renderInput}
            renderOption={renderOption || defaultRenderOption}
        />
    );
});

Autocomplete.displayName = 'Autocomplete';
